// rraApprovePicker — RRA v2.2 (WEL-180): approving an animal request no longer approves "the animal
// on the case" (there is none). On a bound pre-lease application staff PICK the animal(s) it covers
// from the household's roster (AppFolio animals + tenant-intake animals), or type one that is not
// on file yet; the picked animals get the $0 exemption on the lease and the rest stay chargeable.
//
// This module is the pure half of that: when the picker opens, what it offers and pre-selects, the
// body that goes to Approve / "Change animals", and the sentence staff read after an approval
// (including the tenancy outcomes: the household animal form went out, is already open, or is off).
// Type-only imports of the client, so it loads under plain `tsx --test`.
//
//   cd apps/web && npx tsx --test src/lib/rraApprovePicker.test.ts
//
// Names, kinds and breeds only — the roster's machine ids ride back to the API and are never shown.

import { animalWords, isAnimalItem } from './rraRequests';
import type {
  RraApproveAnimal,
  RraCaseView,
  RraDecisionResponse,
  RraItemView,
  RraRosterAnimal,
} from './rraStaffClient';

/** The API caps the picked list at 10. */
export const PICKER_MAX = 10;

export const PICKER_HEADING = 'Which animal(s) does this cover?';
export const PICKER_HELP =
  'Pick the animals this approval covers. They are listed at no animal rent or deposit on the lease; the household’s other animals stay chargeable.';
/** A tenancy approval: the covered animals go on the household animal form at no additional charge. */
export const PICKER_HELP_TENANCY =
  'Pick the animals this approval covers. The household’s animal form lists them at no additional animal rent or deposit; any other animal they add is a normal new animal.';
/** The words under the picker's heading for this kind of case. */
export function pickerHelp(kind: RraCaseView['kind']): string {
  return kind === 'tenancy' ? PICKER_HELP_TENANCY : PICKER_HELP;
}
/** The tag on a roster row staff already marked `Decision pending` in the lease builder. */
export const PICKER_PENDING_IN_BUILDER = 'marked in the builder';
export const PICKER_ALREADY_ZERO = 'already $0';
export const PICKER_NOT_ON_FILE = '+ Not on file yet';
export const PICKER_UNAVAILABLE =
  'We couldn’t read the household’s animals just now. Add the animal below, or try again in a moment.';
export const PICKER_NONE_ON_FILE = 'No animals are on file for this household yet. Add the animal below.';

// ---------------------------------------------------------------------------
// When the picker applies
// ---------------------------------------------------------------------------

/**
 * Approve opens the picker: a bound animal request (pre-lease, or — RRA v2.2 part B — a tenancy) with no
 * legacy animal on it. An unbound case never does (the animals are optional there and apply at attach).
 * A tenancy picker lists the occupancy's current animals plus the typed row.
 */
export function needsAnimalPicker(
  record: Pick<RraCaseView, 'kind' | 'origin'>,
  item: Pick<RraItemView, 'scope' | 'animal'>,
): boolean {
  return (
    (record.kind === 'pre_lease' || record.kind === 'tenancy') &&
    record.origin !== 'lease_build' &&
    isAnimalItem(item) &&
    !item.animal
  );
}

/** "Change animals": an approved PRE-LEASE request (the replace-animals route is for a lease roster only). */
export function canChangeAnimals(
  record: Pick<RraCaseView, 'kind' | 'origin'>,
  item: Pick<RraItemView, 'scope' | 'animal' | 'status'>,
): boolean {
  return item.status === 'approved' && record.kind === 'pre_lease' && needsAnimalPicker(record, item);
}

// ---------------------------------------------------------------------------
// The roster, as the picker sees it
// ---------------------------------------------------------------------------

export type RosterLoad =
  | { status: 'loading' }
  | { status: 'ready'; animals: RraRosterAnimal[] }
  | { status: 'unavailable' };

/**
 * Every animal the picker can offer: the roster, plus any animal this request already covers that
 * the roster no longer lists (a typed one) — so "Change animals" can never silently drop it.
 */
export function pickerChoices(roster: ReadonlyArray<RraRosterAnimal>, item: Pick<RraItemView, 'itemId' | 'approvedAnimals'>): RraRosterAnimal[] {
  const have = new Set(roster.map((a) => a.id));
  const carried: RraRosterAnimal[] = [];
  for (const a of item.approvedAnimals ?? []) {
    if (!a.id || !a.name || have.has(a.id)) continue;
    have.add(a.id);
    carried.push({
      id: a.id,
      name: a.name,
      ...(a.type ? { type: a.type } : {}),
      ...(a.breed ? { breed: a.breed } : {}),
      source: 'intake',
      exemptByItemId: item.itemId,
    });
  }
  return [...roster, ...carried];
}

/** The picker's editable state. `selected` holds roster ids; the typed row is the "not on file yet" animal. */
export interface PickerState {
  selected: string[];
  typedOn: boolean;
  typedName: string;
  typedType: 'dog' | 'cat' | 'other';
}

/**
 * Opening state. Animals this request already covers are pre-selected; with nothing on the roster
 * to pick from, the typed row is open (it is the only way forward).
 */
export function initialPickerState(
  choices: ReadonlyArray<RraRosterAnimal>,
  item: Pick<RraItemView, 'itemId' | 'approvedAnimals'>,
): PickerState {
  const approvedIds = new Set((item.approvedAnimals ?? []).map((a) => a.id).filter((id): id is string => Boolean(id)));
  // A request not yet approved: the animals staff marked `Decision pending` in the lease builder are
  // exactly the ones it concerns, so they start ticked. (Once approved, the covered animals are the truth.)
  const covered = approvedIds.size > 0 || choices.some((a) => a.exemptByItemId === item.itemId);
  return {
    selected: choices
      .filter((a) => a.exemptByItemId === item.itemId || approvedIds.has(a.id) || (!covered && a.pendingInBuilder === true))
      .map((a) => a.id),
    typedOn: choices.length === 0,
    typedName: '',
    typedType: 'dog',
  };
}

export function toggleSelected(state: PickerState, id: string): PickerState {
  return {
    ...state,
    selected: state.selected.includes(id) ? state.selected.filter((s) => s !== id) : [...state.selected, id],
  };
}

/** The animals the picker would send: the chosen roster animals (roster order), then the typed one when it has a name. */
export function pickerAnimals(choices: ReadonlyArray<RraRosterAnimal>, state: PickerState): RraApproveAnimal[] {
  const out: RraApproveAnimal[] = choices
    .filter((a) => state.selected.includes(a.id))
    .map((a) => ({ id: a.id, name: a.name, ...(a.type ? { type: a.type } : {}), ...(a.breed ? { breed: a.breed } : {}) }));
  const typed = state.typedName.trim();
  if (state.typedOn && typed) out.push({ name: typed, type: state.typedType });
  return out;
}

/** Approve stays disabled until at least one animal is chosen (and not more than the cap). */
export function pickerReady(choices: ReadonlyArray<RraRosterAnimal>, state: PickerState): boolean {
  const n = pickerAnimals(choices, state).length;
  return n >= 1 && n <= PICKER_MAX;
}

/** `Rex · dog · Lab mix` — name, kind, breed; whatever is known. */
export function rosterRowWords(a: Pick<RraRosterAnimal, 'name' | 'type' | 'breed'>): string {
  return [a.name, a.type, a.breed?.trim()].filter(Boolean).join(' · ');
}

/**
 * The tag beside a roster row: `already $0` whenever the animal is already exempt — another request
 * holds it (`exemptByItemId`), or the API says `exempt` for one that names no request (a staff
 * build-time note). An animal THIS request already covers carries none (it is pre-ticked instead).
 */
export function rosterRowTag(a: Pick<RraRosterAnimal, 'exemptByItemId' | 'exempt'>, itemId: string): string | null {
  if (a.exemptByItemId === itemId) return null;
  return a.exempt === true || a.exemptByItemId ? PICKER_ALREADY_ZERO : null;
}

// ---------------------------------------------------------------------------
// What staff read after Approve / Change animals
// ---------------------------------------------------------------------------

export const APPROVE_NOTICES = {
  tenancySent: 'Approved — we sent the household the animal form; the addendum follows when they complete it.',
  priorSendOpen:
    'Approved, but the household already has an animal form open — finish or cancel that one, then run Approve again.',
  notEnabled: 'Approved, but animal-addendum sends are switched off.',
  alreadySent: 'Approved. The animal form already went out for this request.',
  coveredOnFile: 'Approved — the animals it covers are already on the tenancy, so no household form is needed.',
  rosterFailed: 'Approved, but the lease roster update failed — pick the animals again to retry.',
} as const;

/** The machine reasons the household-form send returns, in words (anything else reads generically). */
export function addendumReasonWords(reason: string | undefined): string {
  switch (reason) {
    case 'test_recipient_blocked':
      return 'this is a test case and that address is not an allowed test address';
    case 'submission_failed':
      return 'the send failed';
    // `no_email` is what the API answers; `no_recipients` is the same thing under its older name.
    case 'no_email':
    case 'no_recipients':
      return 'no one in the household has an email on file';
    // Every animal the approval covers is already on the tenancy, so there is no household round to send.
    case 'covered_on_file':
      return 'the animals it covers are already on the tenancy — no form needed';
    default:
      return 'it could not be sent';
  }
}

/** A notice is a success banner; a problem is the red banner (the approval stands, but staff must act). */
export interface ApproveOutcome {
  tone: 'notice' | 'problem';
  text: string;
}

/**
 * The sentence after Approve. The approval itself always stands; `problem` means something after it
 * needs staff (the household form did not go, the lease roster did not update).
 */
export function approveOutcome(args: {
  requesterName: string | undefined;
  res: Pick<
    RraDecisionResponse,
    'approvedAwaitingApplication' | 'approvalEmailSent' | 'rosterError' | 'rosterApplied' | 'addendum' | 'item'
  >;
  /** What the picker sent — used for the sentence when the reply carries no animals. */
  picked?: ReadonlyArray<RraApproveAnimal>;
}): ApproveOutcome {
  const { res } = args;
  const who = args.requesterName?.trim() || 'the requester';

  if (res.approvedAwaitingApplication) {
    return {
      tone: 'notice',
      text:
        res.approvalEmailSent === false
          ? `Approved. We couldn't email ${who}; it applies when they apply.`
          : `Approved. We emailed ${who}; it applies when they apply.`,
    };
  }
  if (res.rosterError) return { tone: 'problem', text: APPROVE_NOTICES.rosterFailed };

  const addendum = res.addendum;
  if (addendum) {
    if (addendum.sent) return { tone: 'notice', text: APPROVE_NOTICES.tenancySent };
    switch (addendum.reason) {
      case 'already_sent':
        return { tone: 'notice', text: APPROVE_NOTICES.alreadySent };
      case 'covered_on_file':
        return { tone: 'notice', text: APPROVE_NOTICES.coveredOnFile };
      case 'prior_send_open':
        return { tone: 'problem', text: APPROVE_NOTICES.priorSendOpen };
      case 'not_enabled':
        return { tone: 'problem', text: APPROVE_NOTICES.notEnabled };
      default:
        return {
          tone: 'problem',
          text: `Approved, but the animal form did not send (${addendumReasonWords(addendum.reason)}) — run Approve again to retry.`,
        };
    }
  }

  const covered = coveredNames(res.item.approvedAnimals, args.picked);
  return {
    tone: 'notice',
    text: `Approved.${covered ? ` ${covered} will be at no animal charge on the lease.` : ''} Holds release once every request on the case is decided.`,
  };
}

/** The notice after "Change animals". */
export function changeAnimalsOutcome(args: {
  res: { item: Pick<RraItemView, 'approvedAnimals'>; rosterApplied: boolean };
  picked: ReadonlyArray<RraApproveAnimal>;
}): ApproveOutcome {
  const covered = coveredNames(args.res.item.approvedAnimals, args.picked);
  if (!args.res.rosterApplied) {
    return { tone: 'problem', text: 'Saved, but the lease roster did not update — try Change animals again.' };
  }
  return { tone: 'notice', text: `Updated.${covered ? ` ${covered} will be at no animal charge on the lease.` : ''}` };
}

function coveredNames(
  approved: RraItemView['approvedAnimals'],
  picked: ReadonlyArray<RraApproveAnimal> | undefined,
): string {
  const animals = approved && approved.length > 0 ? approved : (picked ?? []);
  if (animals.length === 0) return '';
  return animals.map(animalWords).join(', ');
}

// ---------------------------------------------------------------------------
// The approved request's effect, in words (the decision card)
// ---------------------------------------------------------------------------

/**
 * The item's addendum pointer is the HOUSEHOLD animal form (RRA v2.2): it says so (`kind`), or — when
 * the wire omits `kind` — it is a pointer on a tenancy request that carries no staff-typed animal
 * (every pre-v2.2 tenancy approval had an animal and sent the standalone addendum, so a kind-less
 * pointer here can only be the household round).
 */
export function isHouseholdFormPointer(
  record: Pick<RraCaseView, 'kind'>,
  item: Pick<RraItemView, 'animal' | 'addendum'>,
): boolean {
  const a = item.addendum;
  if (!a) return false;
  if (a.kind) return a.kind === 'household_form';
  return record.kind === 'tenancy' && !item.animal;
}

/** Every animal the approval covers is already on the tenancy: the server stamped the item and sent no household round. */
export function isCoveredOnFile(item: Pick<RraItemView, 'addendum'>): boolean {
  return item.addendum?.kind === 'covered_on_file';
}

/** The decision card's line for an approval that needed no household form. */
export const NO_FORM_NEEDED = 'No form needed — the animals it covers are already on the tenancy.';

/** A legacy item (staff typed the animal) keeps the sentence it always had. */
const LEGACY_WAIVED =
  'Animal fees/deposit waived for this animal (charge exemption applied); the standard animal conduct rules still apply like any approved animal.';

/**
 * What an APPROVED request did, for the decision card — or null when it did nothing to animals (a
 * rule change or a physical change touches no animal charge, so it says nothing about one).
 */
export function approvedEffectLine(args: {
  record: Pick<RraCaseView, 'kind'>;
  item: Pick<RraItemView, 'scope' | 'animal' | 'approvedAnimals' | 'addendum'>;
  awaitingApplication: boolean;
}): string | null {
  const { record, item } = args;
  if (!isAnimalItem(item)) return null;
  const covers = (item.approvedAnimals ?? []).length > 0;

  if (args.awaitingApplication) {
    if (covers || item.animal) return 'When they apply, the animal goes on their lease at no extra charge.';
    return 'Once they apply, use “Change animals” to pick which animals this covers.';
  }
  if (record.kind === 'tenancy' && !item.animal) {
    if (isCoveredOnFile(item)) return NO_FORM_NEEDED;
    if (isHouseholdFormPointer(record, item)) {
      return 'The household was sent the animal form at no animal rent or deposit; the addendum follows when they complete it.';
    }
    return item.addendum ? null : 'The household animal form has not gone out yet.';
  }
  if (covers) {
    return 'Animal rent and deposit are waived for the animals above (charge exemption applied); the standard animal conduct rules still apply like any approved animal.';
  }
  if (item.animal) return LEGACY_WAIVED;
  return null;
}

/**
 * An approved tenancy animal request whose household animal form never went out (the send was off,
 * or an earlier form was still open). Approve is safe to run again — the send is claimed once. Any
 * addendum pointer — including `covered_on_file`, where no form was ever needed — means there is
 * nothing to run again.
 */
export function canRunApproveAgain(
  record: Pick<RraCaseView, 'kind'>,
  item: Pick<RraItemView, 'scope' | 'status' | 'addendum'>,
): boolean {
  return record.kind === 'tenancy' && isAnimalItem(item) && item.status === 'approved' && !item.addendum;
}

/**
 * "Run Approve again" on an approval that covers NO animals (a failed save left it approved with no
 * `approvedAnimals` and no addendum): the server needs the picks, so the re-run opens the picker instead
 * of posting an empty body (which answers "Pick the animal(s) this approval covers."). When animals are
 * already stored the re-run posts as before.
 */
export function runApproveAgainNeedsPicker(item: Pick<RraItemView, 'approvedAnimals'>): boolean {
  return (item.approvedAnimals ?? []).length === 0;
}

/** The decision card's line about the addendum pointer. */
export function addendumLine(
  record: Pick<RraCaseView, 'kind'>,
  item: Pick<RraItemView, 'animal' | 'addendum'>,
  fmt: (iso: string | undefined) => string,
): string | null {
  const a = item.addendum;
  if (!a) return null;
  if (isCoveredOnFile(item)) return null; // the effect line already says no form was needed
  if (isHouseholdFormPointer(record, item)) {
    return a.sentAt
      ? `Animal Addendum sent ${fmt(a.sentAt)} — the household completed the animal form; it is out for signatures.`
      : 'The household animal form is out — the addendum follows when they complete it.';
  }
  return `Animal Addendum ${a.sentAt ? `sent ${fmt(a.sentAt)}` : 'created'} — single-signer, tracked on the Requests screen as “Animal addendum”.`;
}
